// SUN-327 W7 (frames 13-15) — the Users & Roles pieces about Reports To and the team.
import { useEffect, useState } from 'react';
import { getUserTeam, fmtRupeesShort, type UserTeamDto } from '@/lib/targets/targetsApi';
import { currentMonthKey, monthLabel } from '@/lib/targets/months';

const first = (name: string) => name.trim().split(/\s+/)[0] ?? name;

/** Note under the Reports To select: Reports To is the team, and old sales keep the manager they had. */
export function ReportsToNote({ personName, managerName }: { personName: string; managerName: string | null }) {
  return (
    <div className="mt-2 rounded-lg border border-[#ece3d4] bg-[#fdfbf6] p-3 text-xs text-gray-600" data-testid="reports-to-note">
      <div className="font-semibold text-gray-800">Reports To = the team</div>
      {managerName ? (
        <div>{personName} → {managerName}. Their sales count in {first(managerName)}'s Team target. Targets read only this field.</div>
      ) : (
        <div>{personName} has no manager set. Targets read only this field.</div>
      )}
      <div className="mt-1 text-gray-500">Saved history is not changed: past sales keep the manager they had.</div>
    </div>
  );
}

/** Info line after a save when the Teams page could not follow Reports To. */
export function teamSyncNotice(teamSync: { result?: string } | null | undefined, managerName: string | null): string | null {
  return teamSync?.result === 'no_team_for_manager' && managerName ? `Teams page not updated: ${managerName} heads no team.` : null;
}

interface DrawerProps {
  userId: string;
  name: string;
  designation?: string | null;
  reportsToName?: string | null;
  onEdit: () => void;
  onClose: () => void;
  /** test seam */
  load?: (userId: string, month: string) => Promise<UserTeamDto>;
}

export function UserTeamDrawer({ userId, name, designation, reportsToName, onEdit, onClose, load = getUserTeam }: DrawerProps) {
  const month = currentMonthKey();
  const [dto, setDto] = useState<UserTeamDto | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let live = true;
    load(userId, month).then((d) => { if (live) setDto(d); }).catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [userId, month, load]);

  return (
    <aside className="fixed inset-y-0 right-0 z-40 w-[360px] overflow-y-auto border-l border-gray-200 bg-white p-5 shadow-xl" aria-label="Team" data-testid="user-team-drawer">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-base font-bold text-gray-900">{name}</div>
          {designation && <div className="text-xs text-gray-500">{designation}</div>}
          {reportsToName && <div className="text-xs text-gray-500">Reports to {reportsToName}</div>}
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={onEdit} className="rounded border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700">Edit</button>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded px-2 py-1 text-xs text-gray-500">✕</button>
        </div>
      </div>

      {failed && <div className="mt-4 text-sm text-red-600">Could not load the team.</div>}
      {!dto && !failed && <div className="mt-4 text-sm text-gray-400">Loading…</div>}
      {dto && (
        <>
          <div className="mt-4 text-xs font-semibold uppercase tracking-wide text-gray-500">Reports to {first(name)} · {dto.count}</div>
          <ul className="mt-1 divide-y divide-gray-100 text-sm">
            {dto.reports.map((r) => (
              <li key={r.id} className="py-1.5">
                <span className="font-medium text-gray-900">{r.name}</span>
                {r.note && <span className="ml-2 text-xs text-gray-500">{r.note}</span>}
              </li>
            ))}
          </ul>
          {(dto.targets.individualRupees != null || dto.targets.teamRupees != null) && (
            <div className="mt-4 rounded-lg bg-[#fdfbf6] p-3 text-sm">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Targets · {monthLabel(month)}</div>
              {dto.targets.individualRupees != null && <div>Individual {fmtRupeesShort(dto.targets.individualRupees)}</div>}
              {dto.targets.teamRupees != null && <div>Team total (they + {dto.count}) {fmtRupeesShort(dto.targets.teamRupees)}</div>}
            </div>
          )}
        </>
      )}
      <p className="mt-5 text-[11px] text-gray-400">To add or move a person: Edit that person › Reports To. Set targets in Performance › Sales Targets.</p>
    </aside>
  );
}
